import * as React from 'react';

import { CodeEditor, Select, Switch } from '@rematter/ui-web';

import { ExpressionInput } from '../../../shared/ExpressionInput';
import { NodeConfigProps } from '../../types';

import { EmailActionConfigType } from './schema';

/**
 * Configuration panel for Email actions.
 * Allows configuring recipient, subject, body, and other email fields.
 */
export const EmailActionConfig: React.FC<
  NodeConfigProps<EmailActionConfigType>
> = ({ config, onUpdate }) => {
  const [to, setTo] = React.useState<string>(config.to || '');
  const [subject, setSubject] = React.useState<string>(config.subject || '');
  const [body, setBody] = React.useState<string>(config.body || '');
  const [bodyType, setBodyType] = React.useState<string>(
    config.bodyType || 'text',
  );
  const [cc, setCc] = React.useState<string>(config.cc || '');
  const [bcc, setBcc] = React.useState<string>(config.bcc || '');
  const [replyTo, setReplyTo] = React.useState<string>(config.replyTo || '');
  const [continueOnFail, setContinueOnFail] = React.useState<boolean>(
    config.continueOnFail || false,
  );

  const handleUpdate = React.useCallback(
    (updates: Partial<EmailActionConfigType>) => {
      onUpdate({ ...config, ...updates });
    },
    [config, onUpdate],
  );

  const handleToChange = React.useCallback(
    (value: string) => {
      setTo(value);
      handleUpdate({ to: value });
    },
    [handleUpdate],
  );

  const handleSubjectChange = React.useCallback(
    (value: string) => {
      setSubject(value);
      handleUpdate({ subject: value });
    },
    [handleUpdate],
  );

  const handleBodyChange = React.useCallback(
    (value: string) => {
      setBody(value);
      handleUpdate({ body: value });
    },
    [handleUpdate],
  );

  const handleBodyTypeChange = React.useCallback(
    (value: string) => {
      setBodyType(value);
      handleUpdate({ bodyType: value as 'text' | 'html' });
    },
    [handleUpdate],
  );

  const handleCcChange = React.useCallback(
    (value: string) => {
      setCc(value);
      handleUpdate({ cc: value });
    },
    [handleUpdate],
  );

  const handleBccChange = React.useCallback(
    (value: string) => {
      setBcc(value);
      handleUpdate({ bcc: value });
    },
    [handleUpdate],
  );

  const handleReplyToChange = React.useCallback(
    (value: string) => {
      setReplyTo(value);
      handleUpdate({ replyTo: value });
    },
    [handleUpdate],
  );

  const handleContinueOnFailChange = React.useCallback(
    (value: boolean) => {
      setContinueOnFail(value);
      handleUpdate({ continueOnFail: value });
    },
    [handleUpdate],
  );

  return (
    <div className="@EmailActionConfig space-y-4">
      <div>
        <ExpressionInput
          label="To"
          value={to}
          onChange={handleToChange}
          placeholder="recipient@example.com or {{trigger.email}}"
          description="Recipient email address (supports expressions)"
        />
      </div>

      <div>
        <ExpressionInput
          label="Subject"
          value={subject}
          onChange={handleSubjectChange}
          placeholder="Email subject or {{trigger.subject}}"
          description="Email subject line (supports expressions)"
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium">Body Type</label>
        <Select
          placeholder="Select body type..."
          value={bodyType}
          onChange={handleBodyTypeChange}
          options={[
            { key: 'text', label: 'Plain Text' },
            { key: 'html', label: 'HTML' },
          ]}
        />
      </div>

      <CodeEditor
        label="Body"
        value={body}
        onChange={handleBodyChange}
        language="text"
        placeholder="Email body content... Use {{variable}} for dynamic values"
        description="Email body content (supports expressions with {{ }} syntax)"
        height="200px"
        enableInterpolation
      />

      <div className="border-t pt-4">
        <p className="mb-3 text-sm font-medium">Optional Fields</p>

        <div className="space-y-3">
          <ExpressionInput
            label="CC"
            value={cc}
            onChange={handleCcChange}
            placeholder="cc@example.com"
            description="Carbon copy recipients (comma-separated)"
          />

          <ExpressionInput
            label="BCC"
            value={bcc}
            onChange={handleBccChange}
            placeholder="bcc@example.com"
            description="Blind carbon copy recipients (comma-separated)"
          />

          <ExpressionInput
            label="Reply To"
            value={replyTo}
            onChange={handleReplyToChange}
            placeholder="noreply@example.com"
            description="Reply-to email address"
          />
        </div>
      </div>

      <div className="border-t pt-4">
        <p className="mb-3 text-sm font-medium">Error Handling</p>

        <div className="rounded-lg border bg-neutral/5 p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm">Continue on failure</p>
              <p className="text-xs text-neutral/60">
                If enabled, the workflow will continue even if this action fails
              </p>
            </div>
            <Switch
              isSelected={continueOnFail}
              onChange={handleContinueOnFailChange}
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
        <p className="text-xs text-primary">
          <strong>Tip:</strong> Use expressions like{' '}
          {'{{trigger.data.customerEmail}}'} to dynamically populate email
          fields from trigger data.
        </p>
      </div>
    </div>
  );
};
